import { useMemo, useRef } from "react";

import { TimePickerInput } from "@/components/fields/date-time-picker";

export interface TimeOfDayFieldProps {
  /** 24-hour "HH:MM" (seconds tolerated on input, dropped on output). */
  value: string;
  onChange: (next: string) => void;
}

/** Parse "HH:MM"/"HH:MM:SS" into a throwaway Date carrying just the time. */
function parseToDate(value: string): Date {
  const [hours, minutes] = value.split(":");
  const date = new Date();
  date.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  return date;
}

function toHHMM(date: Date | undefined): string {
  if (!date) return "00:00";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * 24-hour time entry (no AM/PM) built from the repo's `TimePickerInput`: two
 * compact HH : MM boxes you type into or step with the arrow keys. `picker=
 * "hours"` is fixed 00–23, so the widget is 24-hour regardless of the viewer's
 * locale — unlike the native `<input type="time">`, whose 12/24h display Chrome
 * ties to the OS locale and cannot be overridden.
 */
export function TimeOfDayField({ value, onChange }: TimeOfDayFieldProps) {
  const date = useMemo(() => parseToDate(value), [value]);
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);

  const setDate = (next: Date | undefined) => onChange(toHHMM(next));

  return (
    <div className="flex w-fit items-center gap-1 rounded-md border p-1">
      <TimePickerInput
        picker="hours"
        date={date}
        setDate={setDate}
        ref={hourRef}
        aria-label="Hours"
        onRightFocus={() => minuteRef.current?.focus()}
      />
      <span className="text-muted-foreground">:</span>
      <TimePickerInput
        picker="minutes"
        date={date}
        setDate={setDate}
        ref={minuteRef}
        aria-label="Minutes"
        onLeftFocus={() => hourRef.current?.focus()}
      />
    </div>
  );
}
